/* eslint-disable no-nested-ternary */
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState, useRef } from 'react';
import { Box, Paper } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'store';
import { activeAction, cleanOpenedAction, openDrawer } from 'store/slices/menu';
import { useConfirmationModalContext } from 'hooks/useConfirmationModal';
import { generateClearedRecordData, splitHeaders } from 'ui-component/records/utils/headerHelpers';
import { IObjectValues, IRecordField, IRecordFieldsList, IRecordHeaders } from 'ui-component/records/types';
import { IFormatDataValues, LogReportType } from 'ui-component/RecordView/types';
import { generateInitialState, getDataToCreate, getDataToUpdate } from 'ui-component/RecordView/utils';
import {
    getAditionalFieldsValues,
    getObjectValuesFromListValues,
    PanelTitle,
    LogReportMode,
    ButtonTypeLog,
    ActivateSaveButtonFrom,
    formatObjPropertyToVariableName,
    formatLabel
} from 'ui-component/RecordView/PropertiesPanel/utils';
import { CreatePanelForm, EditPanelForm } from 'ui-component/RecordView/PropertiesPanel/EditPanel/v2';
import SideBar from 'ui-component/RecordView/PropertiesPanel/SideBar';
import { filterNotAllowedFields, getEditableField } from 'ui-component/records/utils';
import { useRecordData } from 'ui-component/RecordView/PropertiesPanel/hooks';
import { QUERY_GET_OBJECT_PROPERTIES } from 'graphql/queries/customObjects';
import { FindObjectProperty, FindObjectPropertyVariables, ObjectProperty } from 'views/backoffice/CustomObjects/types';
import { useLazyQuery } from '@apollo/client';
import { DynamicObject } from 'views/TenantProfileSettings/components/types';
import { generateCurrentValuesByPropertyId } from 'views/backoffice/CustomLists/utils';
import { FooterHistoryPanel } from 'ui-component/RecordView/PropertiesPanel/FooterHistoryPanel';
import { DROPDOWN_COLUMNS } from 'ui-component/records/utils/columnHelpers';
import { RECORD_VIEW_COMPONENTS } from 'ui-component/RecordView/RecordViewCore';
import { PanelHeader } from 'ui-component/RecordView/PropertiesPanel/PanelHeader';
import { AllPanelContainer } from 'ui-component/RecordView/PropertiesPanel/AllPanelContainer';
import { emptyRequiredFieldsErrorMessage } from 'views/DocumentViewer/utils';
import { useSelectedAndDefaultListTypesValues } from 'hooks/useSelectedAndDefaultListTypesValues';

export interface IForm {
    recordType?: number;
    recordNumber: string;
    statusId: number | null;
    problemCodeId: number | null;
    approvedBy: number | null;
    recordDate: string | null;
    dueDate: string | null;
    poNumber: string;
    assignedTo: number | null;
}

export interface PropertiesPanelProps {
    loading?: boolean;
    recordData?: IRecordHeaders | null;
    recordType?: string;
    isCreate?: boolean;
    hasFile?: boolean;
    onSubmit: (recordType: number, data: IFormatDataValues, showAlerts: boolean) => Promise<IRecordHeaders | boolean>;
    isEditMode?: any;
    onUploadFile: () => void;
    // PDF Controllers
    onToggleLineItems?: () => void;
    fileAdded?: boolean;
    onToggleComments?: () => void;
    onToggleAttachments?: () => void;
    showLogCommentsPanel?: boolean;
    setShowLogCommentsPanel?: (value: boolean) => void;
    showLineItemsPanel?: boolean;
    setShowLogLineItemsPanel?: (value: boolean) => void;
    showLogAttachmentsPanel?: boolean;
    setShowLogAttachmentsPanel?: (value: boolean) => void;
    recordId?: number;
    hideSidebar?: boolean;
    initialEditMode?: boolean;
    modalMode?: boolean;
    onChangeDisabledSubmit?: (value: boolean) => void;
    components?: Partial<(typeof RECORD_VIEW_COMPONENTS)[number]>[];
    rightHandPanelWidth?: number;
    setActivateRightHandPanel: (value: boolean) => void;
    activateRightHandPanel: boolean;
    onToggleDataFocusView?: () => void;
    openDataFocusViewDialog?: boolean;
}

export type PropertiesPanelRef = {
    handleSubmit: () => Promise<boolean>;
    validateObjectChanges: () => Promise<void>;
};

export const PropertiesPanelv3Content = forwardRef(
    (
        {
            loading,
            recordData = null,
            recordType,
            isCreate,
            onSubmit,
            onUploadFile,
            hasFile = false,
            isEditMode,
            onToggleLineItems,
            fileAdded = false,
            onToggleComments,
            onToggleAttachments,
            showLogCommentsPanel,
            setShowLogCommentsPanel,
            showLineItemsPanel,
            setShowLogLineItemsPanel,
            showLogAttachmentsPanel,
            setShowLogAttachmentsPanel,
            recordId,
            hideSidebar,
            initialEditMode,
            modalMode,
            onChangeDisabledSubmit,
            components = [...RECORD_VIEW_COMPONENTS],
            rightHandPanelWidth = 0,
            setActivateRightHandPanel,
            activateRightHandPanel,
            onToggleDataFocusView,
            openDataFocusViewDialog
        }: PropertiesPanelProps,

        ref
    ) => {
        const modal = useConfirmationModalContext();
        const dispatch = useDispatch();
        const [changePanel, setChangePanel] = useState<PanelTitle>(isCreate || initialEditMode ? PanelTitle.Edit : PanelTitle.View);
        const [logReportMode, setLogReportMode] = useState<LogReportMode>(LogReportMode.All);
        const [showFullHistory, setShowFullHistory] = useState(false);
        const [recordTypeId, setRecordTypeId] = useState(recordType);
        const [initialFormState, setInitialFormState] = useState<Partial<CreatePanelForm>>({});

        const methods = useForm<CreatePanelForm | EditPanelForm>({
            mode: 'onChange'
        });

        const { fieldAccess, headers, loading: loadingRecordData } = useRecordData({ recordTypeId: Number(recordTypeId) });

        const checkAllowedFields = useMemo(() => filterNotAllowedFields(fieldAccess), [fieldAccess]);

        const { baseFieldHeaders, additionalFieldHeaders } = useMemo(
            () => splitHeaders(headers || null, checkAllowedFields),
            [checkAllowedFields, headers]
        );

        const isAttachmentFieldAllowed = Object.values(additionalFieldHeaders).filter((value) => value?.dataType === 'attachment');

        const { isLoading: loadingListValues, listValuesByListId } = useSelectedAndDefaultListTypesValues(headers, recordData || undefined);

        // Objects States
        const [objectProperties, setObjectProperties] = useState<(ObjectProperty & { value: string; objectName: string })[]>([]);
        const [selectedObjectToEdit, setSelectedObjectToEdit] = useState<{ id: number; name: string; isEditable: boolean } | null>(null);
        const [inMemoryObjectProperties, setInMemoryObjectProperties] = useState<{ [key: string]: any }>({});
        const [prevChangePanel, setPrevChangePanel] = useState<PanelTitle>(PanelTitle.View);
        const [buttonTypeLog, setButtonTypeLog] = useState<LogReportType>(ButtonTypeLog.Detail);

        // Objects Queries
        const [getObjectProperties, { loading: loadingObjectProperties }] = useLazyQuery<FindObjectProperty, FindObjectPropertyVariables>(
            QUERY_GET_OBJECT_PROPERTIES
        );

        const additionalFieldsFormattedNamesToHeaders: Record<string, Partial<IRecordField>> = useMemo(
            () =>
                Object.keys(additionalFieldHeaders)?.reduce(
                    (acc, cur) => ({
                        ...acc,
                        [formatObjPropertyToVariableName(cur)]: { actualFieldName: cur, ...additionalFieldHeaders[cur] }
                    }),
                    {}
                ),
            [additionalFieldHeaders]
        );
        // Objects Control
        const handleOpenViewObjectPanel = async (
            field: IRecordField,
            fieldName: string,
            editable: boolean,
            selectedObject?: Pick<DynamicObject, 'id' | 'objectValues' | 'objectDefinition'> | null
        ) => {
            const isListOfObjects = !!field?.listType?.objectDefinition?.id;

            const objectDefinitionIds = selectedObject
                ? +selectedObject.objectDefinition.id
                : isListOfObjects
                ? field.listType?.objectDefinition?.id
                : field.objectDefinition.id;

            const { data: objectPropertiesData } = await getObjectProperties({
                variables: { data: { objectDefinitionIds } }
            });

            // When the user selects an object but want to see it before save it in the field
            if (selectedObject) {
                const currentValuesByPropertyId = generateCurrentValuesByPropertyId(selectedObject.objectValues);
                setObjectProperties(
                    objectPropertiesData?.findObjectProperty.map((el) => ({
                        ...el,
                        value: currentValuesByPropertyId[+el.id]?.value || '',
                        objectName: fieldName
                    })) || []
                );

                setSelectedObjectToEdit({ id: 0, name: fieldName, isEditable: field.gridEditable });
                setChangePanel(PanelTitle.ViewObject);
                return;
            }

            const fieldData = recordData?.additionalFields?.find((additionalField) => {
                const fieldId = additionalField.tag.split(';')[2];
                return Number(fieldId) === Number(field.id);
            });

            setSelectedObjectToEdit({ id: fieldData?.id ? Number(fieldData?.id) : 0, name: fieldName, isEditable: field.gridEditable });

            const valuesFromObjectList = getObjectValuesFromListValues(listValuesByListId, field?.listType?.id, fieldData?.value);

            const values = isListOfObjects ? (valuesFromObjectList as IObjectValues[]) : fieldData?.objectValue?.objectValues || [];

            if (isCreate) {
                const mappedProperties = objectPropertiesData?.findObjectProperty
                    .map((property) => ({
                        ...property,
                        value: '',
                        objectName: fieldName
                    }))
                    .sort((a, b) => {
                        const orderA = a.order;
                        const orderB = b.order;
                        if (orderA < orderB) {
                            return -1;
                        }
                        if (orderA > orderB) {
                            return 1;
                        }
                        return 0;
                    });
                setObjectProperties(inMemoryObjectProperties[fieldName] || mappedProperties);
            } else {
                let mappedProperties = [];
                if (inMemoryObjectProperties[fieldName]) {
                    mappedProperties = { ...inMemoryObjectProperties[fieldName], objectName: fieldName };
                } else {
                    mappedProperties =
                        objectPropertiesData?.findObjectProperty
                            .map((property) => ({
                                ...property,
                                value: values.find((el) => Number(el.objectProperty.id) === Number(property.id))?.value || '',
                                objectName: fieldName
                            }))
                            .filter((property) => property.enabled)
                            .sort((a, b) => {
                                const orderA = a.order;
                                const orderB = b.order;
                                if (orderA < orderB) {
                                    return -1;
                                }
                                if (orderA > orderB) {
                                    return 1;
                                }
                                return 0;
                            }) || [];
                }
                setObjectProperties(mappedProperties || []);
            }

            setPrevChangePanel(changePanel);
            setChangePanel(changePanel === PanelTitle.Edit && editable ? PanelTitle.EditObject : PanelTitle.ViewObject);
        };

        const handleUpdateObject = async (newProperties: (ObjectProperty & { value: string })[]) => {
            const stateInsert: { [key: string]: any } = {};
            stateInsert[String(selectedObjectToEdit?.name)] = newProperties;
            setInMemoryObjectProperties((prev) => ({ ...prev, ...stateInsert }));
            setPrevChangePanel(PanelTitle.View);
            setChangePanel(PanelTitle.Edit);
        };

        const handleSaveEditObjectFormActions = async (values: { [key: string]: string }, modalTitle: string) => {
            await modal.showConfirmation({
                title: modalTitle,
                content: '',
                loadingText: 'Saving Changes',
                forwardButtonText: 'Save changes',
                actionButtonText: 'Discard Changes',
                onForward: async () => {
                    modal.toogleLoading(true);
                    const newObjectProperties = objectProperties.map((item: ObjectProperty & { value: string }) => {
                        if (values[item.name]) {
                            return {
                                ...item,
                                value: values[item.name].toString()
                            };
                        }
                        return item;
                    });
                    setObjectProperties(newObjectProperties as (ObjectProperty & { value: string; objectName: string })[]);
                    handleUpdateObject(newObjectProperties);
                    if (!isCreate) {
                        const newProperties: { [key: string]: any } = {};
                        newProperties[String(selectedObjectToEdit?.name)] = newObjectProperties;
                        const formData = { ...methods.getValues(), ...inMemoryObjectProperties, ...newProperties };
                        const { clearedData } = getClearedDataWithActualFieldNames(formData, values);
                        // const clearedData = generateClearedRecordData(initialFormState, formData, values);

                        await onSubmit(
                            Number(recordTypeId),
                            getDataToUpdate(
                                clearedData,
                                baseFieldHeaders,
                                additionalFieldHeaders,
                                recordData?.additionalFields || [],
                                fieldAccess
                            ),
                            true
                        );
                    }
                    modal.toogleLoading(false);
                },
                onAction: () => setChangePanel(prevChangePanel),
                showCheckbox: true,
                actionType: `${modalTitle === 'Do you want to save this changes?' ? `save` : `discard`}_object_changes`,
                sectionTitle: 'record_viewer',
                recordType: '',
                hideCloseBtn: true
            });
        };

        const toggleEditForm = () => {
            let newPanelState: PanelTitle = PanelTitle.View;
            const isObjectPanel = changePanel === PanelTitle.EditObject || changePanel === PanelTitle.ViewObject;

            if (isObjectPanel) {
                newPanelState = changePanel === PanelTitle.EditObject ? PanelTitle.ViewObject : PanelTitle.EditObject;
            } else {
                newPanelState = changePanel === PanelTitle.Edit ? PanelTitle.View : PanelTitle.Edit;
            }

            setChangePanel(newPanelState);
            setActivateRightHandPanel(true);
            if (newPanelState === PanelTitle.Edit) {
                dispatch(activeAction('record_view_edit'));
                methods.reset({ ...initialFormState });
            } else dispatch(cleanOpenedAction());
        };

        const toggleLogReport = () => {
            switch (changePanel) {
                case PanelTitle.EditObject:
                case PanelTitle.ViewObject:
                    setLogReportMode(LogReportMode.Objects);
                    break;
                case PanelTitle.LogReport:
                    setLogReportMode(LogReportMode.Empty);
                    break;
                default:
                    setLogReportMode(LogReportMode.All);
                    break;
            }

            if (changePanel === PanelTitle.LogReport) {
                setChangePanel(PanelTitle.View);
                setActivateRightHandPanel(false);
            } else {
                setChangePanel(PanelTitle.LogReport);
            }

            setShowLogCommentsPanel?.(false);
            setShowLogLineItemsPanel?.(false);
            setShowLogAttachmentsPanel?.(false);
        };

        const handleOnToggleComments = () => {
            onToggleComments?.();
            setLogReportMode((prevMode) => (prevMode !== LogReportMode.Comments ? LogReportMode.Comments : LogReportMode.All));
        };

        const handleOnToggleLineItems = () => {
            onToggleLineItems?.();
            setLogReportMode((prevMode) => (prevMode !== LogReportMode.LineItems ? LogReportMode.LineItems : LogReportMode.All));
        };

        const handleOnToggleAttachments = () => {
            onToggleAttachments?.();
            setLogReportMode((prevMode) => (prevMode !== LogReportMode.Attachments ? LogReportMode.Attachments : LogReportMode.All));
        };

        const dynamicTitleFooterButton = useMemo(() => {
            if (logReportMode === LogReportMode.Comments) {
                return 'Show full comments history';
            }

            if (logReportMode === LogReportMode.LineItems) {
                return 'Show full line items history';
            }

            return 'Show full history';
        }, [logReportMode]);

        const handleClickCancel = async () => {
            const { clearedData } = getClearedDataWithActualFieldNames();

            if (Object.entries(clearedData).filter(([_key, val]) => val !== null).length === 0) {
                setChangePanel(PanelTitle.View);
                dispatch(cleanOpenedAction());
            } else
                await modal.showConfirmation({
                    title: 'Are you sure you want to discard your changes?',
                    content: '',
                    loadingText: 'Saving Changes',
                    forwardButtonText: 'Discard Changes',
                    actionButtonText: 'Save changes',
                    onForward: async () => {
                        setChangePanel(PanelTitle.View);
                        dispatch(cleanOpenedAction());
                    },
                    onAction: handleSubmit,
                    showCheckbox: true,
                    actionType: 'discard',
                    sectionTitle: 'record_viewer',
                    recordType: '',
                    hideCloseBtn: true
                });
        };

        // Submit handlers
        const handleClickSave = async () => {
            const formDataWithFormattedFieldNames = methods.getValues();
            const formDataWithActualFieldNames = Object.keys(formDataWithFormattedFieldNames).reduce(
                (acc, cur) => ({
                    ...acc,
                    [additionalFieldsFormattedNamesToHeaders[cur as string]?.actualFieldName || cur]: formDataWithFormattedFieldNames[cur]
                }),
                {}
            );
            const requiredFields = Object.keys(headers || {}).filter((field) => {
                const header = (headers as Record<string, any>)[field];
                if (header && header.required && header.gridEditable && getEditableField(field, fieldAccess)) {
                    return formatLabel(field);
                }
                return null;
            });

            console.log('headers', headers);
            console.log('requiredFields', requiredFields);

            const emptyRequiredFields = (requiredFields || []).filter((field: string) => {
                const value = formDataWithActualFieldNames[field as keyof typeof formDataWithActualFieldNames];
                return !value || value === -1;
            });

            if (emptyRequiredFields.length > 0) {
                emptyRequiredFields.forEach((field: string) => {
                    methods.setError(field, {
                        type: 'manual',
                        message: emptyRequiredFieldsErrorMessage(headers?.[field]?.dataType || '')
                    });
                });
                const firstErrorField = emptyRequiredFields[0];
                const node = inputRefs.current[firstErrorField];
                if (node && typeof node.scrollIntoView === 'function') {
                    node.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }
            } else if (changePanel === PanelTitle.EditObject && objectPanelRef.current) {
                objectPanelRef.current.validateObjectChanges();
            } else if (await methods.trigger()) {
                await modal.showConfirmation({
                    title: hasFile ? 'Do you want to save this changes?' : "This record doesn't contain a document",
                    content: '',
                    loadingText: 'Saving Changes',
                    forwardButtonText: hasFile ? 'Save changes' : 'Save Anyway',
                    actionButtonText: hasFile ? 'Discard Changes' : 'Attach a Document',
                    onForward: async () => {
                        await handleSubmit(formDataWithActualFieldNames);
                    },
                    onAction: hasFile ? async () => setChangePanel(PanelTitle.View) : onUploadFile,
                    showCheckbox: true,
                    actionType: isCreate ? 'create' : 'edit',
                    sectionTitle: 'record_viewer_save',
                    recordType: '',
                    hideCloseBtn: true
                });
            }
        };

        /**
         * Get the cleared data with the actual field names
         * @param initialFormData - The initial form data
         * @param objectValues - The object values
         * @returns { formDataWithFormattedFieldNames, formDataWithActualFieldNames, initialFormStateWithActualFieldNames, clearedData }
         */
        const getClearedDataWithActualFieldNames = (initialFormData?: Record<string, any>, objectValues: Record<string, any> = {}) => {
            const formDataWithFormattedFieldNames = initialFormData || methods.getValues();
            const formDataWithActualFieldNames = Object.keys(formDataWithFormattedFieldNames).reduce(
                (acc, cur) => ({
                    ...acc,
                    [additionalFieldsFormattedNamesToHeaders[cur as string]?.actualFieldName || cur]: formDataWithFormattedFieldNames[cur]
                }),
                {}
            );

            const initialFormStateWithActualFieldNames = Object.keys(initialFormState).reduce(
                (acc, cur) => ({
                    ...acc,
                    [additionalFieldsFormattedNamesToHeaders[cur as string]?.actualFieldName || cur]: initialFormState[cur]
                }),
                {}
            );
            const clearedData = generateClearedRecordData(initialFormStateWithActualFieldNames, formDataWithActualFieldNames, objectValues);

            return { formDataWithFormattedFieldNames, formDataWithActualFieldNames, initialFormStateWithActualFieldNames, clearedData };
        };

        const handleSubmit = async (formDataConverted?: Record<string, any>) => {
            modal.toogleLoading(true);
            const { clearedData, formDataWithActualFieldNames } = getClearedDataWithActualFieldNames(formDataConverted);

            if (!isCreate && !modalMode && Object.entries(clearedData).filter(([_key, val]) => val !== null).length === 0 && !fileAdded) {
                setChangePanel(PanelTitle.View);
                return false;
            }

            const res = await onSubmit(
                Number(recordTypeId),
                isCreate
                    ? getDataToCreate(
                          formDataWithActualFieldNames,
                          baseFieldHeaders,
                          additionalFieldHeaders,
                          fieldAccess,
                          inMemoryObjectProperties
                      )
                    : getDataToUpdate(
                          clearedData,
                          baseFieldHeaders,
                          additionalFieldHeaders,
                          recordData?.additionalFields || [],
                          fieldAccess
                      ),
                true
            );
            modal.toogleLoading(false);
            if (res && !modalMode) setChangePanel(PanelTitle.View);
            return res || true;
        };

        const handleToggleLogReport = () => {
            setChangePanel((s) => (s === PanelTitle.LogReport ? PanelTitle.View : PanelTitle.LogReport));
            setActivateRightHandPanel(true);
            setLogReportMode(LogReportMode.All);
        };

        // create form for edit record
        useEffect(() => {
            if (!recordData || !additionalFieldHeaders || !baseFieldHeaders || isCreate) return;

            const aditionalFields =
                additionalFieldHeaders && recordData?.additionalFields
                    ? getAditionalFieldsValues(additionalFieldsFormattedNamesToHeaders, recordData?.additionalFields)
                    : {};
            const formState = Object.keys(baseFieldHeaders)
                .sort()
                .reduce((acc, key) => {
                    const value =
                        ['string', 'number', 'boolean', 'date', 'datetime'].includes(baseFieldHeaders[key]?.dataType || '') &&
                        !DROPDOWN_COLUMNS.includes(key as (typeof DROPDOWN_COLUMNS)[number])
                            ? recordData[key as keyof IRecordHeaders]
                            : (recordData[key as keyof IRecordHeaders] as Record<string, any>)?.id;
                    return { ...acc, [key]: value };
                }, {});

            setInitialFormState({
                ...formState,
                ...(aditionalFields || {})
            });
            methods.reset({
                ...formState,
                ...(aditionalFields || {})
            });
        }, [additionalFieldHeaders, recordData, baseFieldHeaders, isCreate, methods, additionalFieldsFormattedNamesToHeaders]);

        // create form for new record
        useEffect(() => {
            if (isCreate && baseFieldHeaders && changePanel === PanelTitle.Edit) {
                const initialState = generateInitialState(baseFieldHeaders, recordType);
                setInitialFormState(initialState);
                if (!methods.formState.defaultValues) methods.reset(initialState);
            }
        }, [baseFieldHeaders, changePanel, isCreate, methods, recordType]);

        useEffect(() => {
            if (!isEditMode) return;
            if (changePanel === PanelTitle.Edit || changePanel === PanelTitle.EditObject || changePanel === PanelTitle.ViewObject)
                isEditMode(true);
            else isEditMode(false);
        }, [changePanel, isEditMode]);

        useEffect(() => {
            if (isCreate) return;
            if (showLogCommentsPanel) {
                setChangePanel(PanelTitle.LogReport);
                setLogReportMode(LogReportMode.Comments);
                setShowLogLineItemsPanel?.(false);
                setShowLogAttachmentsPanel?.(false);
            } else if (showLineItemsPanel) {
                setChangePanel(PanelTitle.LogReport);
                setLogReportMode(LogReportMode.LineItems);
                setShowLogCommentsPanel?.(false);
                setShowLogAttachmentsPanel?.(false);
            } else if (showLogAttachmentsPanel) {
                setChangePanel(PanelTitle.LogReport);
                setLogReportMode(LogReportMode.Attachments);
                setShowLogCommentsPanel?.(false);
                setShowLogLineItemsPanel?.(false);
            } else if (!initialEditMode) {
                if (!isCreate) setChangePanel(PanelTitle.View);
                setLogReportMode(LogReportMode.All);
                setShowLogCommentsPanel?.(false);
                setShowLogLineItemsPanel?.(false);
                setShowLogAttachmentsPanel?.(false);
            }
        }, [
            showLogCommentsPanel,
            showLineItemsPanel,
            isCreate,
            setShowLogLineItemsPanel,
            setShowLogCommentsPanel,
            initialEditMode,
            showLogAttachmentsPanel,
            setShowLogAttachmentsPanel
        ]);

        // triggers the onChangeDisabledSubmit function
        useEffect(() => {
            if (onChangeDisabledSubmit) {
                onChangeDisabledSubmit(
                    loading ||
                        loadingRecordData ||
                        loadingListValues ||
                        loadingObjectProperties ||
                        Object.keys(methods.formState.errors).length > 0
                );
            }
        }, [loading, loadingListValues, loadingObjectProperties, loadingRecordData, methods.formState.errors, onChangeDisabledSubmit]);

        useImperativeHandle(ref, () => ({
            handleSubmit,
            validateObjectChanges: () => {
                if (objectPanelRef.current && changePanel === PanelTitle.EditObject) {
                    return objectPanelRef.current.validateObjectChanges();
                }
                return Promise.resolve();
            }
        }));

        const isObjectPanel = changePanel === PanelTitle.EditObject || changePanel === PanelTitle.ViewObject;
        const inputRefs = useRef<Record<string, HTMLDivElement | null>>({});
        const objectPanelRef = useRef<{ getValues: () => any; validateObjectChanges: () => void }>(null);
        const objectEditingRestricted = !selectedObjectToEdit?.isEditable || false;

        return (
            <Paper
                data-testid="properties-panel"
                sx={{
                    height: '100%',
                    display: isObjectPanel ? 'flex' : 'block',
                    flexDirection: 'column',
                    width: '100%',
                    zIndex: 1000
                }}
            >
                <FormProvider {...methods}>
                    <Box display="flex" flexDirection="row" sx={{ height: '100%', '& > div': { maxHeight: '100%' } }}>
                        {(isCreate || activateRightHandPanel) && (
                            <Box width={changePanel === PanelTitle.LogReport || (modalMode && hideSidebar) ? '100%' : 'calc(100% - 50px)'}>
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: isCreate
                                            ? 'calc(100% - 5px)'
                                            : changePanel === PanelTitle.LogReport
                                            ? 'calc(100% - 60px)'
                                            : 'calc(100% - 5px)',
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }}
                                >
                                    <PanelHeader
                                        changePanel={changePanel}
                                        logReportMode={logReportMode}
                                        selectedObjectToEdit={selectedObjectToEdit}
                                        toggleEditForm={toggleEditForm}
                                        onChangeSelection={setLogReportMode}
                                        toggleLogReport={toggleLogReport}
                                        buttonTypeLog={buttonTypeLog}
                                        setButtonTypeLog={setButtonTypeLog}
                                        isCreate={isCreate}
                                        onClickHeader={() => {
                                            setPrevChangePanel(PanelTitle.View);
                                            setChangePanel(prevChangePanel);
                                        }}
                                        rightHandPanelWidth={rightHandPanelWidth}
                                        recordTypeId={Number(recordTypeId)}
                                    />
                                    <Box
                                        sx={{
                                            overflowY: 'auto',
                                            overflowX: 'hidden'
                                        }}
                                    >
                                        <AllPanelContainer
                                            curPanel={changePanel}
                                            loading={!!loading || loadingRecordData || loadingListValues || loadingObjectProperties}
                                            fieldAccessList={fieldAccess}
                                            recordData={recordData}
                                            listValuesByListId={listValuesByListId}
                                            headerData={headers as IRecordFieldsList | null}
                                            toggleEditForm={toggleEditForm}
                                            handleOpenViewObjectPanel={handleOpenViewObjectPanel}
                                            recordTypeId={recordTypeId}
                                            inMemoryObjectProperties={inMemoryObjectProperties}
                                            rightHandPanelWidth={rightHandPanelWidth}
                                            handleClosePanel={() => {
                                                setChangePanel(PanelTitle.View);
                                                setObjectProperties([]);
                                            }}
                                            objectProperties={objectProperties}
                                            handleSaveEditObjectFormActions={handleSaveEditObjectFormActions}
                                            recordId={Number(recordId)}
                                            showFullHistory={showFullHistory}
                                            onCloseFullHistory={() => setShowFullHistory(false)}
                                            logReportMode={logReportMode}
                                            handleChangeSelection={setLogReportMode}
                                            buttonTypeLog={buttonTypeLog}
                                            setButtonTypeLog={setButtonTypeLog}
                                            toggleLogReport={handleToggleLogReport}
                                            isCreate={isCreate}
                                            onChangeRecordType={(val) => setRecordTypeId(val)}
                                            inputRefs={inputRefs}
                                            objectEditingRestricted={objectEditingRestricted}
                                            objectPanelRef={objectPanelRef}
                                            additionalFieldsFormattedNamesToHeaders={additionalFieldsFormattedNamesToHeaders}
                                        />
                                    </Box>
                                </Box>
                                {changePanel === PanelTitle.LogReport && (
                                    <FooterHistoryPanel
                                        label={dynamicTitleFooterButton as unknown as string}
                                        onShowFullHistory={() => {
                                            setShowFullHistory(true);
                                            dispatch(openDrawer(false));
                                        }}
                                    />
                                )}
                            </Box>
                        )}
                        {!hideSidebar && (
                            <SideBar
                                isCreate={isCreate}
                                changePanel={changePanel}
                                logReportMode={logReportMode}
                                toggleLogReport={toggleLogReport}
                                onToggleLineItems={components.includes('LINE_ITEMS') ? handleOnToggleLineItems : undefined}
                                onToggleComments={components.includes('COMMENTS') ? handleOnToggleComments : undefined}
                                onToggleAttachments={components.includes('ATTACHMENTS') ? handleOnToggleAttachments : undefined}
                                hideButtons={!components.includes('BUTTONS')}
                                hideLogReport={!components.includes('LOG_REPORT')}
                                isObjectPanel={isObjectPanel}
                                onGoBack={() => {
                                    setPrevChangePanel(PanelTitle.View);
                                    setChangePanel(prevChangePanel);
                                    dispatch(cleanOpenedAction());
                                }}
                                recordTypeId={Number(recordType)}
                                isAttachmentFieldAllowed={!!isAttachmentFieldAllowed?.length}
                                handleClickCancel={handleClickCancel}
                                handleClickSave={handleClickSave}
                                validateObjectChanges={() => {
                                    if (objectPanelRef.current && changePanel === PanelTitle.EditObject) {
                                        objectPanelRef.current.validateObjectChanges();
                                    }
                                }}
                                loading={loading || loadingRecordData}
                                recordDataId={Number(recordId)}
                                businessEntity={recordData?.businessEntity ?? null}
                                setActivateRightHandPanel={setActivateRightHandPanel}
                                activateRightHandPanel={activateRightHandPanel}
                                onToggleDataFocusView={onToggleDataFocusView}
                                from={ActivateSaveButtonFrom.DocumentViewer}
                                objectEditingRestricted={objectEditingRestricted}
                            />
                        )}
                    </Box>
                </FormProvider>
            </Paper>
        );
    }
);
